import { z } from "zod";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils.js";
import { canonicalTwinBoundaryJson } from "./twin-boundary-json.js";
const field = z.string().regex(/^[a-z][a-z0-9_]{0,126}$/u);
const digest = z.string().regex(/^[0-9a-f]{64}$/u);
const markers = z.array(z.string().min(1)).min(1).max(32)
    .refine((values) => new Set(values).size === values.length, "Markers must be unique.");
const currencies = z.array(z.string().regex(/^[a-z]{3}$/u)).min(1).max(64)
    .refine((values) => values.every((value, index) => index === 0 || values[index - 1] < value), "Currencies must be sorted and unique.");
/** The frozen connector semantic expression, including its exact marker binding. */
export const twinMeasureExpressionSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("direct_measure"), amount_field: field }).strict(),
    z.object({ kind: z.literal("absolute_magnitude"), amount_field: field }).strict(),
    z.object({ kind: z.literal("marker_signed_magnitude"), amount_field: field,
        marker_field: field, positive_markers: markers, negative_markers: markers,
        convention: z.literal("debit_positive_credit_negative") }).strict().superRefine((value, context) => {
        if (value.positive_markers.some((marker) => value.negative_markers.includes(marker))) {
            context.addIssue({ code: "custom", message: "Positive and negative markers must be disjoint." });
        }
    }),
    z.object({ kind: z.literal("marker_gated_measure"), amount_field: field,
        marker_field: field, included_markers: markers }).strict(),
    // A stored minor-unit amount in the standard unit of its row's currency: two decimal
    // places unless the currency is listed, and null when the row names no currency.
    z.object({ kind: z.literal("minor_unit_amount"), amount_field: field, currency_field: field,
        zero_decimal_currencies: currencies, three_decimal_currencies: currencies,
    }).strict().superRefine((value, context) => {
        if (value.zero_decimal_currencies.some((code) => value.three_decimal_currencies.includes(code))) {
            context.addIssue({ code: "custom", message: "Zero-decimal and three-decimal currencies must be disjoint." });
        }
    }),
]);
export const twinMeasureTransformationSchema = z.enum([
    "absolute", "negate", "round", "add", "subtract", "multiply", "divide", "coalesce", "case",
    "count_rows", "count", "count_distinct", "average", "minimum", "maximum",
]);
const declared = z.object({
    kind: z.literal("declared"),
    sourceRelation: z.string().regex(/^authoring_source\.[a-z][a-z0-9_]{0,126}$/u),
    projectionContractSha256: digest,
    semanticField: field,
    semanticContractVersion: z.number().int().positive().safe(),
    semanticContractSha256: digest,
    expression: twinMeasureExpressionSchema,
}).strict();
function basisSchema(depth) {
    const leaves = [
        z.object({ kind: z.literal("not_applicable") }).strict(),
        z.object({ kind: z.literal("unknown"), reason: z.string().min(1).max(2_000) }).strict(),
        declared,
    ];
    if (depth === 0)
        return z.union(leaves);
    return z.union([...leaves, z.object({
            kind: z.literal("transformed"), operation: twinMeasureTransformationSchema,
            expressionSha256: digest,
            // A CASE's 64 branches and its otherwise, as the worker's measure_basis.py bounds them.
            operands: z.array(basisSchema(depth - 1)).max(65),
        }).strict().superRefine(({ operation, operands }, context) => {
            const count = operands.length;
            const valid = operation === "count_rows" ? count === 0
                : ["absolute", "negate", "round", "count", "count_distinct", "average", "minimum", "maximum"].includes(operation)
                    ? count === 1 : ["add", "subtract", "multiply", "divide"].includes(operation)
                    ? count === 2 : count >= 1;
            if (!valid)
                context.addIssue({ code: "custom", path: ["operands"],
                    message: "Measure transformation operand count does not match its operation." });
        })]);
}
/** Bound recursion before parsing, including malicious recursive provenance trees. */
export const twinMeasureBasisSchema = basisSchema(12);
export function measureBasisSha256(basis) {
    return bytesToHex(sha256(utf8ToBytes(canonicalTwinBoundaryJson(twinMeasureBasisSchema.parse(basis)))));
}
